/**
 * The field catalogue for one screen — loaded once per mount, indexed, and
 * patched in place when a person corrects a field.
 *
 * A hook rather than a module-level cache, deliberately: the catalogue is
 * computed on read (coverage over the people as they are now, every column of
 * every ready upload), and a module-scope copy would both go stale on the next
 * upload and outlive the session that loaded it — the house rule against
 * per-tenant state at module scope.
 *
 * `applyField` is how a correction lands without a second round trip: `PUT
 * /fields/:key/settings` answers with the field as it now reads, and replacing
 * that one entry is exactly what re-reading the catalogue would have produced
 * for it. The builder uses it when somebody answers "day first or month
 * first?" from inside the field picker, so the rule they were writing stops
 * reporting the question the moment it is answered.
 */
import type { Field, FieldCatalogueResponse } from '@wend/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { indexCatalogue, type FieldIndex } from '../components/rules/rule-model';
import { fetchFieldCatalogue } from './api/fields';
import { useAsyncData } from './use-async-data';

export interface FieldCatalogueState {
  readonly catalogue: FieldCatalogueResponse | null;
  readonly index: FieldIndex | null;
  readonly error: unknown;
  readonly loadingFirst: boolean;
  readonly refreshing: boolean;
  readonly reload: () => void;
  readonly applyField: (field: Field) => void;
}

export function useFieldCatalogue(): FieldCatalogueState {
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData((signal) => fetchFieldCatalogue(signal), []);
  const [patches, setPatches] = useState<ReadonlyMap<string, Field>>(new Map());

  // A fresh read from the server supersedes every local patch.
  useEffect(() => {
    setPatches(new Map());
  }, [data]);

  const catalogue = useMemo<FieldCatalogueResponse | null>(() => {
    if (data === null) return null;
    if (patches.size === 0) return data;
    return { ...data, fields: data.fields.map((field) => patches.get(field.key) ?? field) };
  }, [data, patches]);

  const index = useMemo(() => (catalogue === null ? null : indexCatalogue(catalogue)), [catalogue]);

  const applyField = useCallback((field: Field) => {
    setPatches((current) => new Map(current).set(field.key, field));
  }, []);

  return { catalogue, index, error, loadingFirst, refreshing, reload, applyField };
}
